import classNames from 'classnames';
import type { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { S26 } from '@sorare/core/src/atoms/icons/S26';
import { SimpleRadio } from '@sorare/core/src/atoms/inputs/SimpleRadio';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXS } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { glossary } from '@sorare/core/src/lib/glossary';

import pack from './assets/pack.webp';

const Root = styled.label`
  width: 100%;
  position: relative;
  max-width: calc(80 * var(--unit));
  & > * {
    width: 100%;
  }
  transition: all 0.1s ease-in-out;
  &:hover {
    transform: scale(1.02);
  }
  cursor: pointer;
  color: var(--c-white);
`;

const ItemHeader = styled.div`
  position: relative;
  height: calc(5 * var(--unit));
  align-self: stretch;
`;

const RadioWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  color: var(--c-nd-600);

  &.hidden {
    visibility: hidden;
  }
`;

const BottomBanner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--quadruple-unit);
  width: 100%;
  color: var(--c-common-tertiary);
  background: var(--c-common);
`;

const StyledBrandXS = styled(BrandXS)`
  line-height: 30px;
`;

const PackImg = styled.img`
  width: 80%;
`;

type Props = {
  selected: boolean;
  hideSelect?: boolean;
  onSelect: () => void;
};

const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;

export const CommonMode = ({
  selected,
  hideSelect,
  onSelect,
  children,
}: PropsWithChildren<Props>) => {
  return (
    <Root>
      <Bezel borderColor={selected ? 'var(--c-common)' : 'var(--c-nd-150)'}>
        <StyledTextureBackground
          background="wave1"
          mode="light"
          color="var(--c-nl-400)"
          gradientOpacity={0.8}
        />
        <ItemHeader>
          <RadioWrapper className={classNames({ hidden: hideSelect })}>
            <SimpleRadio
              id="common-radio"
              checked={selected}
              checkedColor="var(--c-white)"
              onClick={onSelect}
            />
          </RadioWrapper>
        </ItemHeader>
        <Vertical gap={2}>
          <Vertical center>
            <BrandL className="text-center">
              <FormattedMessage
                id="ChooseGameMode.common.freePacks"
                defaultMessage="Free Packs"
              />
            </BrandL>
            <Horizontal>
              <BrandXS>
                <FormattedMessage
                  id="ChooseGameMode.common.subtitle"
                  defaultMessage="Learn the game every day"
                />
              </BrandXS>
            </Horizontal>
            {children}
          </Vertical>
          <Vertical center>
            <PackImg src={pack} alt="" />
          </Vertical>
        </Vertical>
        <BottomBanner>
          <Marquee gap={1} duration="3s" disableAnimation={!selected}>
            <Horizontal center gap={0.5}>
              <S26 />
              <StyledBrandXS>
                <FormattedMessage {...glossary.beginner} />
              </StyledBrandXS>
            </Horizontal>
          </Marquee>
        </BottomBanner>
      </Bezel>
    </Root>
  );
};
